/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  AbstractFormComponent,
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
} from '@gh/builder-shared';
import { AelEvents, ContactTermsReceivables } from '@gh/builder-shared-data';
import {
  ActionFlow,
  EnumDictionary,
  getInvalidFields,
  getRouteInitData,
  IS_REGION_AU_OR_NZ,
  IS_REGION_US,
  isEntityInvalid,
} from '@gh/common-shared';
import { DialogService, NotificationService, yesAnswer, yesNoConfirmation } from '@gh/core-ui';
import { FocusedCell } from '@gh/core-ui-grid';
import { filter$ } from '@gh/rx/operators';

import { filter, first } from 'lodash';

import { getExcludedCcRangeId, hasInvalidExcludedCcRange, SystemVarsReceivables } from '../../resources';
import { ExcludedCcRange } from '../../resources/model/system-vars.model';
import { SystemVarsReceivablesTabBusinessService } from '../../resources/system-vars-receivables-tab-business.service';
import { SystemVarsReceivablesFormState } from './system-vars-receivables-form.service';

enum SystemVarsReceivablesTab {
  CommonSettings = 'COMMON_SETTINGS',
  LendersSchedule = 'LENDERS_SCHEDULE',
}

@Component({
  selector: 'gh-system-vars-receivables-tab',
  template: require('./system-vars-receivables-tab.component.html'),
  viewProviders: [SystemVarsReceivablesFormState],
})
export class SystemVarsReceivablesTabComponent extends AbstractFormComponent<SystemVarsReceivables> implements OnInit {
  selectedTab = SystemVarsReceivablesTab.CommonSettings;

  isRegionUs = IS_REGION_US;
  isRegionAuOrNz = IS_REGION_AU_OR_NZ;
  focusedCcRangeCell?: FocusedCell;

  original: SystemVarsReceivables;
  termsReceivables: EnumDictionary<ContactTermsReceivables>;

  constructor(private route: ActivatedRoute,
              private userContext: BuilderUserContext,
              private dialogs: DialogService,
              private notifications: NotificationService,
              private navigations: BuilderNavigationService,
              public form: SystemVarsReceivablesFormState,
              private business: SystemVarsReceivablesTabBusinessService,
              private commonActions: CommonActions,
              private pipelines: FormActionPipelines) {
    super();
  }

  get completeFlow(): ActionFlow<FormFlowState<SystemVarsReceivables>> {
    return this.pipelines.complete({
      validate: this.commonActions.validateWithEffect(
        ({ value }) => hasInvalidExcludedCcRange(value),
        this.navigateToInvalidCell.bind(this)),
      execute: this.commonActions.execute(({ value }) => this.business.update(value)),
      finish: ({ value }) => {
        this.original = value;
        this.form.build(value);
      },
    });
  }

  ngOnInit(): void {
    const { details, termsReceivables } = getRouteInitData(this.route);

    this.original = details;
    this.termsReceivables = termsReceivables;
    this.form.build(details);
  }

  onReset(): void {
    this.dialogs.confirmation(yesNoConfirmation('msg_reset')).pipe(
      filter$(yesAnswer))
      .subscribe(() => this.form.build(this.original));
  }

  goToAel(): void {
    this.navigations.goToAel({
      entityId: this.userContext.office && this.userContext.office.id,
      entityType: 'OFFICE',
      eventTypeIds: [AelEvents.Office.EditedReceivables],
      label: 'lbl_receivables',
    });
  }

  private navigateToInvalidCell(): void {
    const firstInvalidRange = first(filter<ExcludedCcRange>(
      this.form.submitValue.excludedCostCenters,
      isEntityInvalid));
    const firstInvalidField = firstInvalidRange ? first(getInvalidFields(firstInvalidRange)) : void 0;

    if (firstInvalidRange && firstInvalidField) {
      this.notifications.error('msg_he_incorrect_data_in_tables');
      this.selectedTab = SystemVarsReceivablesTab.LendersSchedule;
      this.focusedCcRangeCell = { id: getExcludedCcRangeId(firstInvalidRange), field: firstInvalidField };
    }
  }
}
